import { match } from "path-to-regexp";
import React, {
  createContext,
  useContext,
  useLayoutEffect,
  useReducer,
} from "react";

import { UserContext } from "@providers/user-provider";

import MissingBoundary from "./404";
import history from "./history";
import MissingError from "./missing-error";

type RouteParams = {
  path: string;
  pathParams: Record<string, string>;
  searchParams: Record<string, string | string[]>;
  hash: string;
};

type RouteConfig = {
  path: string;
  authRoute?: boolean;
  action: (params: RouteParams, context: any) => React.ReactNode;
};

type RouteContextType = {
  routes: RouteConfig[];
  history: typeof history;
};

type RouterProps = {
  routes: RouteConfig[];
  children: React.ReactNode;
};

const RouteContext = createContext<RouteContextType | null>(null);

const groupParamsByKey = (
  params: URLSearchParams,
): Record<string, string | string[]> => {
  return [...params.entries()].reduce(
    (acc, tuple) => {
      // getting the key and value from each tuple
      const [_key, val] = tuple;

      // get in form 'key', rather than 'key[]' for arrays
      const key = _key.replace(/\[\]$/, "");

      if (Object.hasOwn(acc, key)) {
        // if the current key is already an array, we'll add the value to it
        if (Array.isArray(acc[key])) {
          acc[key] = [...acc[key], val];
        } else {
          // if it's not an array, but contains a value, we'll convert it into an array
          // and add the current value to it
          acc[key] = [acc[key], val];
        }
      } else {
        // plain assignment if no special case is present
        acc[key] = val;
      }

      return acc;
    },
    {} as Record<string, string | string[]>,
  );
};

const render = (
  routes: RouteConfig[],
  context: any,
): React.ReactNode | undefined => {
  const { pathname: path, search, hash } = history.location;

  for (const route of routes) {
    const result = match(route.path, { decode: decodeURIComponent })(path);

    if (result) {
      // Params from path
      const pathParams = result.params as Record<string, string>;

      // Search params
      const searchParams = groupParamsByKey(
        new URLSearchParams(history.location.search),
      );

      const params: RouteParams = {
        path,
        pathParams,
        searchParams,
        hash,
      };

      // Check auth and redirect to signin page if necessary
      if (route.authRoute) {
        if (!context.user?.id) {
          history.push(`/signin?redirect=${encodeURIComponent(path + search)}`);
          return <></>;
        }
      }

      return route.action(params, context);
    }
  }
};

const Router = (props: RouterProps) => {
  const [, forceUpdate] = useReducer((x) => x + 1, 0);

  useLayoutEffect(() => {
    const unListen = history.listen(() => {
      forceUpdate();
    });

    return () => {
      unListen();
    };
  }, []);

  return (
    <RouteContext.Provider
      value={{
        routes: props.routes,
        history,
      }}
    >
      {props.children}
    </RouteContext.Provider>
  );
};

const Route = () => {
  const userContext = useContext(UserContext);
  const routeContext = useContext(RouteContext);

  if (!routeContext) {
    throw new Error("Route must be used within a Router");
  }

  const renderedRoute = render(routeContext.routes, userContext);
  if (renderedRoute) return renderedRoute;
  throw new MissingError(history.location.pathname);
};

export { Route, Router, RouteContext, MissingBoundary, render };
